import { create } from 'zustand'
import { api } from '../services/api'

// Knowledge Catalog filter + results store. Not persisted — filter state
// should reset on page reload. Deep-linking happens via URL search params
// in the CatalogBrowser page, not here.
const DEFAULT_FILTERS = {
  dimensions: {},       // { tool: ["figma", "miro"], work_type: ["design"] }
  entry_type: null,     // single entry_type slug or null
  search: '',
  initiative_id: null,
  person_id: null,
}

function buildQueryString(state) {
  const params = new URLSearchParams()

  Object.entries(state.filters.dimensions || {}).forEach(([dimension, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      params.set(`dimension[${dimension}]`, values.join(','))
    }
  })

  if (state.filters.entry_type) params.set('entry_type', state.filters.entry_type)
  if (state.filters.search)     params.set('search', state.filters.search)
  if (state.filters.initiative_id) params.set('initiative_id', state.filters.initiative_id)
  if (state.filters.person_id)  params.set('person_id', state.filters.person_id)

  // status is a view mode, not a filter — only emit when it's not the
  // published default so the API falls back correctly and the URL stays clean.
  if (state.status && state.status !== 'published') params.set('status', state.status)

  params.set('sort', state.sort)
  params.set('page', state.page)
  params.set('per_page', state.per_page)

  return params.toString()
}

export const useCatalogStore = create((set, get) => ({
  // ---- Filters ----
  filters: { ...DEFAULT_FILTERS },
  sort: 'created_at',
  status: 'published',   // view mode: 'published' | 'draft' | 'all'
  page: 1,
  per_page: 25,

  // ---- Results ----
  entries: [],
  facets: {},
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,

  // ---- Filter actions ----
  setDimensionFilter: (dimension, values) => {
    set((state) => ({
      filters: {
        ...state.filters,
        dimensions: {
          ...state.filters.dimensions,
          [dimension]: Array.isArray(values) ? values : [values],
        },
      },
      page: 1,
    }))
  },

  toggleDimensionValue: (dimension, value) => {
    set((state) => {
      const current = state.filters.dimensions[dimension] || []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]

      const dimensions = { ...state.filters.dimensions }
      if (next.length === 0) {
        delete dimensions[dimension]
      } else {
        dimensions[dimension] = next
      }

      return {
        filters: { ...state.filters, dimensions },
        page: 1,
      }
    })
  },

  clearDimensionFilter: (dimension) => {
    set((state) => {
      const dimensions = { ...state.filters.dimensions }
      delete dimensions[dimension]
      return {
        filters: { ...state.filters, dimensions },
        page: 1,
      }
    })
  },

  clearAllFilters: () => {
    set({
      filters: { ...DEFAULT_FILTERS },
      page: 1,
    })
  },

  setEntryType: (type) => {
    set((state) => ({
      filters: { ...state.filters, entry_type: type || null },
      page: 1,
    }))
  },

  setInitiativeFilter: (id) => {
    set((state) => ({
      filters: { ...state.filters, initiative_id: id || null },
      page: 1,
    }))
  },

  setSearch: (query) => {
    set((state) => ({
      filters: { ...state.filters, search: query || '' },
      page: 1,
    }))
  },

  setSort: (sort) => {
    set({ sort, page: 1 })
  },

  setStatus: (status) => {
    set({ status: status || 'published', page: 1 })
  },

  setPage: (page) => {
    const next = Number.isInteger(page) && page >= 1 ? page : 1
    set({ page: next })
  },

  // ---- Fetching ----
  // Monotonic id so overlapping fetches (rapid facet toggles, debounced
  // search, pagination) can't finish out of order and overwrite newer
  // results with stale ones — only the latest request may write state.
  _fetchSeq: 0,

  fetchEntries: async () => {
    const requestId = get()._fetchSeq + 1
    set({ loading: true, error: null, _fetchSeq: requestId })
    try {
      const query = buildQueryString(get())
      const path = query ? `/api/v1/catalog?${query}` : '/api/v1/catalog'
      const response = await api.get(path)

      if (get()._fetchSeq !== requestId) return // superseded by a newer fetch
      set({
        entries:    response.entries || [],
        facets:     response.facets || {},
        totalCount: response.meta?.total_count || 0,
        totalPages: response.meta?.total_pages || 0,
        loading:    false,
      })
    } catch (err) {
      if (get()._fetchSeq !== requestId) return
      set({ loading: false, error: err.message || 'Failed to load catalog' })
    }
  },

  // Exposed so tests / pages can inspect the exact query string the store
  // would build for its current state without triggering a network call.
  _buildQueryString: () => buildQueryString(get()),
}))
